import { useState } from 'react';
import { ExternalLink, Music, Plus, Bookmark, Heart, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { TierBadge } from './TierBadge';

// Platform icons and colors
const PLATFORM_STYLES = {
  spotify: { name: 'Spotify', color: '#1DB954', bgColor: 'rgba(29, 185, 84, 0.1)' },
  apple_music: { name: 'Apple Music', color: '#FA243C', bgColor: 'rgba(250, 36, 60, 0.1)' },
  tidal: { name: 'TIDAL', color: '#00FFFF', bgColor: 'rgba(0, 255, 255, 0.1)' },
  soundcloud: { name: 'SoundCloud', color: '#FF5500', bgColor: 'rgba(255, 85, 0, 0.1)' },
  youtube: { name: 'YouTube', color: '#FF0000', bgColor: 'rgba(255, 0, 0, 0.1)' }
};

export const TrackSharePost = ({ 
  post, 
  user, 
  isAdmin, 
  onReact, 
  onComment, 
  onSaveToPlaylist,
  onExpandComments,
  expanded = false,
  isTrending = false
}) => {
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  
  const platform = PLATFORM_STYLES[post.track_platform] || PLATFORM_STYLES.spotify;
  const totalReactions = Object.values(post.reactions || {}).reduce((sum, arr) => sum + arr.length, 0);
  const isOwnPost = post.user_id === user?.id;
  
  // Check if user has reacted with any emoji
  const userReactions = Object.entries(post.reactions || {})
    .filter(([_, users]) => users.includes(user?.id))
    .map(([emoji]) => emoji);

  return (
    <div 
      className={`rounded-2xl p-4 transition-all ${isTrending ? 'ring-2 ring-yellow-500' : ''}`}
      style={{ 
        background: '#1a1a1a', 
        border: `1px solid ${isTrending ? '#D4AF37' : 'rgba(255,255,255,0.1)'}` 
      }}
      data-testid={`track-post-${post.id}`}
    >
      {/* Trending Badge */}
      {isTrending && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-yellow-500/30">
          <span className="text-lg">🔥</span>
          <span className="text-sm font-bold" style={{ color: '#D4AF37' }}>Trending Track</span>
        </div>
      )}
      
      {/* User Header */}
      <div className="flex items-center gap-3 mb-3">
        <div 
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${post.user_tier === 'elite' ? 'ring-2 ring-purple-500' : ''}`}
          style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
        >
          {(post.user_name || 'U').charAt(0)}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{post.user_name}</span>
            {post.user_tier && <TierBadge tier={post.user_tier} />}
          </div>
          <span className="text-xs text-gray-400">
            {new Date(post.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
      
      {/* Track Card */}
      <div 
        className="rounded-xl p-4 mb-3 flex gap-4"
        style={{ background: platform.bgColor, border: `1px solid ${platform.color}30` }}
      >
        {/* Artwork */}
        <div 
          className="w-20 h-20 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
          style={{ background: 'rgba(0,0,0,0.3)' }}
        >
          {post.track_artwork ? (
            <img src={post.track_artwork} alt={post.track_title} className="w-full h-full object-cover" />
          ) : (
            <Music size={32} style={{ color: platform.color }} />
          )}
        </div>
        
        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-lg truncate" style={{ color: '#ffffff' }}>
            {post.track_title || 'Unknown Track'}
          </h4>
          <p className="text-sm truncate" style={{ color: '#a3a3a3' }}>
            {post.track_artist || 'Unknown Artist'}
          </p>
          
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {/* Platform Badge */}
            <span 
              className="text-xs px-2 py-1 rounded-full font-medium"
              style={{ background: `${platform.color}20`, color: platform.color }}
            >
              {platform.name}
            </span>
            
            {/* Genre */}
            {post.track_genre && (
              <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-300">
                {post.track_genre}
              </span>
            )}
            
            {/* BPM */}
            {post.track_bpm && (
              <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-300 flex items-center gap-1">
                <Clock size={10} />
                {post.track_bpm} BPM
              </span>
            )}
          </div>
          
          {/* Open in Platform Button */}
          <a
            href={post.track_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 text-sm font-medium transition-colors hover:opacity-80"
            style={{ color: platform.color }}
          >
            <ExternalLink size={14} />
            Open in {platform.name}
          </a>
        </div>
      </div>
      
      {/* Description */}
      {post.content && (
        <p className="text-sm mb-4" style={{ color: '#e0e0e0' }}>{post.content}</p>
      )}
      
      {/* Engagement Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
        {totalReactions > 0 && (
          <span className="flex items-center gap-1">
            <Heart size={14} />
            {totalReactions}
          </span>
        )}
        {post.comment_count > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare size={14} />
            {post.comment_count}
          </span>
        )}
        {post.save_count > 0 && (
          <span className="flex items-center gap-1">
            <Bookmark size={14} />
            {post.save_count} saved
          </span>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-3 border-t border-white/10">
        {/* Reactions */}
        <div className="flex items-center gap-1 mr-2">
          {['🎧', '🔥', '💯'].map((emoji) => (
            <button
              key={emoji}
              onClick={() => onReact(post.id, emoji)}
              className={`p-2 rounded-lg transition-all hover:bg-white/10 ${userReactions.includes(emoji) ? 'bg-white/20' : ''}`}
              data-testid={`react-${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
        
        {/* Comment Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onExpandComments(post.id)}
          className="text-gray-400 hover:text-white"
          data-testid="comment-btn"
        >
          <MessageSquare size={16} className="mr-1" />
          {post.comment_count || 0}
        </Button>
        
        {/* Add to Playlist Button */}
        {!isOwnPost && (
          <div className="relative ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddToPlaylist(!showAddToPlaylist)}
              className="text-gray-400 hover:text-[#D4AF37]"
              data-testid="add-to-playlist-btn"
            >
              <Plus size={16} className="mr-1" />
              Add to Playlist
            </Button>
            
            {showAddToPlaylist && (
              <div 
                className="absolute right-0 top-10 z-20 min-w-[200px] rounded-xl shadow-lg p-2"
                style={{ background: '#2a2a2a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
              >
                {onSaveToPlaylist && typeof onSaveToPlaylist === 'function' && (
                  <button
                    onClick={() => {
                      onSaveToPlaylist(post.id);
                      setShowAddToPlaylist(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm flex items-center gap-2"
                  >
                    <Bookmark size={14} style={{ color: '#D4AF37' }} />
                    Save to My Playlist
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
